import React, { useCallback, useEffect, useState } from "react";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { IPropertyChainData, useChainData } from "../../components/chain-data-provider";
import { IPropertyLoyaltyBrand, useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import { useApiClient } from "../../lib/http";
import { getActiveChildren, updatePropertyDefaultBrand } from "./ghaLoyaltySwitch.api";
import { Container, PropertyItem, PropertyList, StyledButton } from "./styles";

export interface Item {
  value: string;
  label: string;
  checked: boolean;
}

function getInitialState({
  chainData,
  activeChildren,
}: {
  chainData: IPropertyChainData;
  activeChildren: string[];
}): Record<string, Item> {
  return chainData.children.reduce(
    (acc, ticker) => ({
      ...acc,
      [ticker]: { label: ticker, value: ticker, checked: activeChildren.includes(ticker) },
    }),
    {}
  );
}

function GHALoyaltySwitch({
  chainData,
  activeChildren,
  brandTicker,
  refreshActiveChildren,
}: {
  chainData: IPropertyChainData;
  activeChildren: string[];
  brandTicker: string;
  refreshActiveChildren: () => Promise<void>;
}) {
  const [checkedState, setCheckedState] = useState(getInitialState({ chainData, activeChildren }));
  const [loading, setLoading] = useState(false);

  const cacheClient = useApiClient();

  const handleOnSubmit = useCallback(async () => {
    const newActive = chainData.children.filter(
      it => checkedState[it].checked && !activeChildren.includes(it)
    );
    const newInactive = chainData.children.filter(
      it => !checkedState[it].checked && activeChildren.includes(it)
    );
    if (!newActive.length && !newInactive.length) {
      return;
    }
    setLoading(true);
    await Promise.all([
      ...newActive.map(it => updatePropertyDefaultBrand(cacheClient, it, brandTicker)),
      ...newInactive.map(it => updatePropertyDefaultBrand(cacheClient, it, null)),
    ]);
    await refreshActiveChildren();
    setLoading(false);
  }, [
    activeChildren,
    brandTicker,
    cacheClient,
    chainData.children,
    checkedState,
    refreshActiveChildren,
  ]);

  const handleChange = useCallback(
    (ticker: string) => () => {
      setCheckedState(prev => {
        const copy = { ...prev };
        copy[ticker] = { ...copy[ticker], checked: !copy[ticker].checked };
        return copy;
      });
      return null;
    },
    []
  );

  return (
    <Container>
      <PropertyList>
        {Object.values(checkedState).map((it: Item) => (
          <PropertyItem>
            <label>
              <input type="checkbox" checked={it.checked} onChange={handleChange(it.value)} />
              {it.label}
            </label>
          </PropertyItem>
        ))}
      </PropertyList>
      <StyledButton disabled={loading} onClick={handleOnSubmit}>
        Guardar
      </StyledButton>
    </Container>
  );
}

function GHALoyaltySwitchWrapper() {
  const chainData: IPropertyChainData | undefined | null = useChainData();
  const loyaltyBrand: IPropertyLoyaltyBrand | undefined | null = useLoyaltyBrand();
  const [activeChildren, setActiveChildren] = useState<string[] | undefined>(undefined);
  useEffect(() => {
    if (!chainData) {
      return undefined;
    }
    getActiveChildren(chainData).then(items => setActiveChildren(items));
  }, [chainData]);

  const refreshActiveChildren = useCallback(async () => {
    if (!chainData) {
      return undefined;
    }
    const items = await getActiveChildren(chainData);
    setActiveChildren(items);
  }, [chainData]);

  if (!chainData || !activeChildren || !loyaltyBrand) {
    return <div>Loading...</div>;
  }
  if (!chainData.isChain) {
    return <div>Not a chain...</div>;
  }
  return (
    <GHALoyaltySwitch
      chainData={chainData}
      activeChildren={activeChildren}
      refreshActiveChildren={refreshActiveChildren}
      brandTicker={loyaltyBrand.ticker}
    />
  );
}

export default withAuthorizationVerifier(React.memo(GHALoyaltySwitchWrapper));
